import { Route, Navigate } from 'react-router-dom';
import Dashboard from '@/components/dashboard/Dashboard';
import MarketAnalysis from '@/components/market-analysis/MarketAnalysis';
import Social from '@/components/social/Social';
import ChatSessionsList from '@/components/chat/ChatSessionsList';
import ChatWindow from '@/components/chat/ChatWindow';
import UserSettings from '@/components/user/UserSettings';
import KernstrategiePage from '@/components/signale/KernstrategiePage';
import EarningsTradesPage from '@/components/signale/EarningsTradesPage';
import TradingRecommendationDetail from '@/components/trading/TradingRecommendationDetail';
import SwingTradesPage from '@/components/signale/SwingTradesPage';
import LongTermTradesPage from '@/components/signale/LongTermTradesPage';
import CoursesView from '@/components/videos/CoursesView';
import CourseDetail from '@/components/videos/CourseDetail';
import ModuleDetail from '@/components/videos/ModuleDetail';
import VideoDetailView from '@/components/videos/VideoDetailView';
import PricingPage from '@/components/checkout/PricingPage';
import DayTradingPage from '@/components/daytrading/DayTradingPage';
import DayTradingParticipantPage from '@/components/daytrading/DayTradingParticipantPage';
import DayTradingModeratorPage from '@/components/daytrading/DayTradingModeratorPage';
import DayTradingObserverPage from '@/components/daytrading/DayTradingObserverPage';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import OptionScreenerPage from '@/components/option-screener/OptionScreenerPage';
import JournalLayout from '@/components/journal/JournalLayout';
import JournalDashboard from '@/components/journal/JournalDashboard';
import OptionTradesList from '@/components/journal/OptionTradesList';
import JournalTradeDetail from '@/components/journal/JournalTradeDetail';
import PositionsView from '@/components/journal/PositionsView';
import JournalGainsCalendar from '@/components/journal/JournalGainsCalendar';
import JournalCapitalLadderPage from '@/components/journal/JournalCapitalLadder';
import JournalTagsPage from '@/components/journal/JournalTagsPage';
import JournalAssignmentWorkflow from '@/components/journal/JournalAssignmentWorkflow';
import JournalReviewsPage from '@/components/journal/JournalReviewsPage';
import JournalImportPage from '@/components/journal/JournalImportPage';
import JournalSettingsPage from '@/components/journal/JournalSettingsPage';
import { TRADING_ROUTES, MARKET_ROUTES, DAY_TRADING_ROUTES, OPTION_SCREENER_ROUTES, JOURNAL_ROUTES } from '@/constants/routes';

// Configuration for user-facing trading recommendation detail routes
const TRADING_DETAIL_ROUTES = [
  {
    path: TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL_PATTERN,
    requiredFeature: 'coreRecommendations' as const,
  },
  {
    path: TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL_PATTERN,
    requiredFeature: 'earningRecommendations' as const,
  },
  {
    path: TRADING_ROUTES.SWING_TRADE_DETAIL_PATTERN,
    requiredFeature: 'swingTradeRecommendations' as const,
  },
  {
    path: TRADING_ROUTES.LONG_TERM_TRADE_DETAIL_PATTERN,
    requiredFeature: 'longTermRecommendations' as const,
  },
] as const;

/**
 * User-facing application routes
 * Includes dashboard, chat, videos, and trading signals
 *
 * Note: This function returns Route elements and should be called as a function,
 * not used as a JSX component: {UserRoutes()} not <UserRoutes />
 */
export default function UserRoutes() {
  return (
    <>
      {/* Root redirect */}
      <Route
        path="/"
        element={<Navigate to="/dashboard" replace />}
      />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={<Dashboard />}
      />

      {/* Market Analysis */}
      <Route
        path={MARKET_ROUTES.ANALYSIS}
        element={<MarketAnalysis />}
      />

      {/* Soziales */}
      <Route
        path="/social"
        element={<Social />}
      />

      {/* User Settings */}
      <Route
        path="/user-settings"
        element={<UserSettings />}
      />

      {/* Chat Routes */}
      <Route
        path="/chats"
        element={
          <ProtectedRoute requiredFeature="chatbot">
            <ChatSessionsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/chats/:sessionId"
        element={
          <ProtectedRoute requiredFeature="chatbot">
            <ChatWindow />
          </ProtectedRoute>
        }
      />

      {/* Trading Signal List Pages */}
      <Route
        path="/signale/kernstrategie"
        element={<KernstrategiePage />}
      />

      <Route
        path="/signale/earning-trades"
        element={<EarningsTradesPage />}
      />

      <Route
        path="/signale/swing-trades"
        element={<SwingTradesPage />}
      />

      <Route
        path="/signale/langzeit-trades"
        element={<LongTermTradesPage />}
      />

      {/* Trading Recommendation Detail Pages */}
      {/* Note: Feature access check moved to TradingRecommendationDetail component to allow free trades */}
      {TRADING_DETAIL_ROUTES.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<TradingRecommendationDetail />}
        />
      ))}

      {/* Video/Course Routes */}
      <Route
        path="/videos"
        element={<CoursesView />}
      />

      <Route
        path="/courses"
        element={<CoursesView />}
      />

      <Route
        path="/courses/:courseId"
        element={<CourseDetail />}
      />

      <Route
        path="/courses/:courseId/modules/:moduleId"
        element={<ModuleDetail />}
      />

      <Route
        path="/courses/:courseId/modules/:moduleId/videos/:videoId"
        element={<VideoDetailView />}
      />

      {/* Option Screener */}
      <Route
        path={OPTION_SCREENER_ROUTES.LIST}
        element={
          <ProtectedRoute requiredFeature="optionScreener">
            <OptionScreenerPage />
          </ProtectedRoute>
        }
      />

      {/* Trading Journal - one guarded layout route, every sub-page is a child */}
      <Route
        path={JOURNAL_ROUTES.DASHBOARD}
        element={
          <ProtectedRoute requiredFeature="journal">
            <JournalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<JournalDashboard />} />
        <Route path={JOURNAL_ROUTES.TRADES} element={<OptionTradesList />} />
        <Route path={JOURNAL_ROUTES.TRADE_DETAIL_PATTERN} element={<JournalTradeDetail />} />
        <Route path={JOURNAL_ROUTES.POSITIONS} element={<PositionsView />} />
        <Route path={JOURNAL_ROUTES.CALENDAR} element={<JournalGainsCalendar />} />
        <Route path={JOURNAL_ROUTES.CAPITAL_LADDER} element={<JournalCapitalLadderPage />} />
        <Route path={JOURNAL_ROUTES.ASSIGNMENTS} element={<JournalAssignmentWorkflow />} />
        <Route path={JOURNAL_ROUTES.REVIEWS} element={<JournalReviewsPage />} />
        <Route path={JOURNAL_ROUTES.TAGS} element={<JournalTagsPage />} />
        <Route path={JOURNAL_ROUTES.IMPORT} element={<JournalImportPage />} />
        <Route path={JOURNAL_ROUTES.SETTINGS} element={<JournalSettingsPage />} />
      </Route>

      {/* Day Trading */}
      <Route
        path={DAY_TRADING_ROUTES.LIST}
        element={<DayTradingPage />}
      />
      <Route
        path={DAY_TRADING_ROUTES.PARTICIPATE_PATTERN}
        element={
          <ProtectedRoute requiredFeature="dayTrading">
            <DayTradingParticipantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={DAY_TRADING_ROUTES.MODERATE_PATTERN}
        element={
          <ProtectedRoute requireEmployee>
            <DayTradingModeratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={DAY_TRADING_ROUTES.OBSERVE_PATTERN}
        element={
          <ProtectedRoute requireAdmin>
            <DayTradingObserverPage />
          </ProtectedRoute>
        }
      />
      {/* Pricing */}
      <Route
        path="/pricing"
        element={<PricingPage />}
      />
    </>
  );
}
